import { defineStore } from 'pinia'
import { computed, ref, Ref } from 'vue'

import { useUserStore } from './user'
import { UserDependentStore } from './UserDependentStore'

import { IBomHeaderDTO } from '@/types/bom'
import { DEBUG, DEBUGC, DEBUG_ERR, MaterialApi } from '@/util'
import '@/types/ArrayExtensions'
import { useEquipmentStore } from './equipment'

/** store (cache) of currently loaded spare parts for the spare part overview 
 * 
 *  Actually loaded spare parts depend on the type of LOGIN:
 * 
 *  - User login: All (visible) spare parts are loaded
 *  - Device login: Only spare parts of the device are loaded
 * 
 */
export const useSparePartsStore = defineStore('sparePartsStore', () => {
    const equipmentStore = useEquipmentStore()

    /********************************************************************************************************************
     STATE
     ********************************************************************************************************************/
    const _currency: Ref<string | null> = ref(null)
    const _showPrices: Ref<boolean> = ref(false)
    const _spareParts: Ref<IBomHeaderDTO[] | null> = ref(null)
    const _loadingState: Ref<string | null> = ref(null)
    const _loadedState: Ref<string | null> = ref(null)

    const state = { _currency, _showPrices, _spareParts, _loadingState, _loadedState }

    /********************************************************************************************************************
     GETTERS
     ********************************************************************************************************************/
    const spareParts = computed(() => _spareParts.value)
    const sparePartMap = computed(() => _spareParts.value?.toMap(sp => sp.material.materialNo.toString()))

    const getters = { spareParts, sparePartMap }

    /********************************************************************************************************************
     ACTIONS
     ********************************************************************************************************************/
    function load() {
        if (userStore.loginType != 'none') {
            loadSpareParts(userStore.loginType, _currency.value, _showPrices.value, equipmentStore.masterData?.equipmentNo)
        }
        else {
            _spareParts.value = []
        }
    }

    function refresh() {
        if (_spareParts.value == null && userStore.loginType != 'none') {
            load()
        }
    }

    function reset() {
        DEBUGC('sparePartsStore: reset', 'orange')
        _spareParts.value = null
        _loadedState.value = null
    }

    const actions = { load, refresh }

    /********************************************************************************************************************
     PRIVATE
     ********************************************************************************************************************/
    function setUserState(currency?: string, showPrices?: boolean) {
        let anyChange = false

        if (showPrices != null && showPrices != _showPrices.value) {
            _showPrices.value = showPrices
            anyChange = true
        }

        if (currency != null && currency != _currency.value) {
            _currency.value = currency
            anyChange = true
        }

        if (anyChange) {
            load()
        }
    }

    function loadSpareParts(loginType: 'user' | 'device',
        currency: string | null,
        showPrices: boolean | null,
        equipmentNo?: number) {

        DEBUGC('sparePartStore: loadSpareParts', 'orange')
        if (currency == null) {
            DEBUGC('sparePartStore: currency not loaded yet', 'orange')
            return // wait until currency has loaded
        }

        const stateToLoad = loginType == 'user'
            ? `${loginType}-${currency}-${showPrices}` // make state independent on currently loaded equipment, to only load all spare parts once
            : `${loginType}-${currency}-${equipmentNo}`

        if (_loadedState.value == stateToLoad) {
            DEBUGC('sparePartStore: state already loaded', 'orange')
            return // already loaded
        }
        if (_loadingState.value == stateToLoad) {
            DEBUGC('sparePartStore: currently loading...', 'orange')
            return // avoid loading the same twice
        }

        _spareParts.value = null
        _loadingState.value = stateToLoad

        // Get source data
        if (loginType == 'user') {
            DEBUGC(`sparePartsStore: Loading data for all spare parts... (${stateToLoad})`, 'darkorange')
            const api = new MaterialApi
            api.allSparePartsWithBoms(currency)
                .then(response => {
                    _loadedState.value = stateToLoad
                    _spareParts.value = response.data
                    DEBUG('sparePartsStore: data =', response.data)
                    DEBUG('*** TOTAL Node count = ', getNodeCount(response.data))
                })
                .catch(() =>
                    DEBUG_ERR('Loading spare parts failed!')
                )
                .finally(() =>
                    _loadingState.value = null
                )
        }
        // Sep 2025: Removed loading of spare parts during device login
        //else if (equipmentNo != null) {
        //    // device login
        //    DEBUG(`sparePartsStore: Loading data for equipment ${equipmentNo}...`)
        //    const api = new EquipmentApi
        //    api.getSparePartFlatList(equipmentNo, currency)
        //        .then(response => {
        //            _loadedState.value = stateToLoad
        //            _spareParts.value = response.data
        //            DEBUG('sparePartsStore: data =', response.data)
        //        })
        //        .catch(() =>
        //            DEBUG_ERR('Loading spare parts failed!')
        //        )
        //        .finally(() =>
        //            _loadingState.value = null
        //        )
    }

    function getNodeCount(boms: IBomHeaderDTO[]): number {
        const result = 1
            + boms.reduce((acc, bom) => acc + getNodeCount(bom.bomInstances.flatMap(instance => instance.components)), 0)
            + boms.reduce((acc, bom) => bom.replacement ? acc + getNodeCount([bom.replacement]) : acc, 0)

        return result
    }

    /********************************************************************************************************************
     MISC
     ********************************************************************************************************************/
    // Setup dependency on user store
    const userStore = useUserStore()

    userStore.$subscribe(() => {
        if (userStore.currency) {
            setUserState(userStore.currency, userStore.permissions?.priceAccessAllowed)
        }
        else {
            reset()
        }
    })

    if (!_currency.value && userStore.userCurrencyHasLoaded) {
        setUserState(userStore.currency, userStore.permissions?.priceAccessAllowed)
    }

    new UserDependentStore(() => load(), () => reset())

    //watchEffect(() => {
    //    if (userStore.loginType === 'user') {
    //        DEBUGC('****** WATCH EFFECT ********', 'coral')
    //        loadSpareParts(userStore.loginType, _currency.value, _showPrices.value, equipmentStore.masterData?.equipmentNo)
    //    }
    //})

    return { ...state, ...getters, ...actions }
})